import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import WidgetWrapper from './WidgetWrapper';
import tagService from '../services/tagService';

const TrendChart = ({ 
  tagIds = [], // Array of tag IDs to monitor
  maxDataPoints = 50,
  dataMode = 'simulation', // 'simulation' or 'historical'
  refreshInterval = 2000, // ms - for legend updates only
  showGrid = true,
  showLegend = true,
  lineColors = ['#1976d2', '#dc004e', '#2e7d32', '#ed6c02', '#0288d1'],
  yAxisMin = 'auto',
  yAxisMax = 'auto',
  title = '',
  // Style properties
  backgroundColor,
  foreColor,
  borderWidth,
  borderColor,
  borderRadius,
  fontSize,
  fontWeight,
  fontFamily,
  opacity,
  ...props 
}) => {
  const canvasRef = useRef(null);
  const [data, setData] = useState(new Map()); // tagId -> array of {timestamp, value} - Static historical data
  // Removed liveValues - using static legend only
  const [containerSize, setContainerSize] = useState({ width: 400, height: 300 });
  const [dataLoaded, setDataLoaded] = useState(false);
  const [availableTags, setAvailableTags] = useState([]); // For tag names in legend
  const containerRef = useRef(null);

  // Update container size when widget is resized (with throttling)
  useEffect(() => {
    let timeoutId;
    
    const updateSize = () => {
      if (timeoutId) clearTimeout(timeoutId);
      
      timeoutId = setTimeout(() => {
        if (containerRef.current) {
          const rect = containerRef.current.getBoundingClientRect();
          const newWidth = Math.floor(rect.width);
          const newHeight = Math.floor(rect.height);
          
          // Only update if size actually changed
          setContainerSize(prev => {
            if (prev.width !== newWidth || prev.height !== newHeight) {
              return { width: newWidth, height: newHeight };
            }
            return prev;
          });
        }
      }, 100); // Throttle resize events
    };
    
    updateSize();
    window.addEventListener('resize', updateSize);
    
    const resizeObserver = new ResizeObserver(updateSize);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      window.removeEventListener('resize', updateSize);
      resizeObserver.disconnect();
    };
  }, []);

  // Load initial historical/simulated data (one-time)
  useEffect(() => {
    if (!tagIds.length) return;
    
    const loadInitialData = async () => {
      const now = Date.now();
      const newData = new Map();
      
      tagIds.forEach((tagId, index) => {
        const tagData = [];
        
        if (dataMode === 'historical') {
          // TODO: Implement historical data fetch from TagLog
          // For now, generate sample historical data
        }
        
        // Generate static data points (simulation or historical fallback)
        for (let i = 0; i < maxDataPoints; i++) {
          const timestamp = now - (maxDataPoints - i - 1) * 60000; // 1 minute intervals
          const value = Math.sin((timestamp / 60000) + index) * 50 + 50 + Math.random() * 20;
          tagData.push({ timestamp, value });
        }
        
        newData.set(tagId, tagData);
      });
      
      setData(newData);
      setDataLoaded(true);
    };
    
    setDataLoaded(false);
    loadInitialData();
  }, [tagIds, dataMode, maxDataPoints]);
  
  // Load available tags for legend names
  useEffect(() => {
    const loadTags = async () => {
      try {
        const tags = await tagService.getTags();
        setAvailableTags(tags);
      } catch (error) {
        console.error('Error loading tags for TrendChart:', error);
      }
    };
    loadTags();
  }, []);

  // Memoize chart drawing function to prevent unnecessary re-renders
  const drawChart = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || data.size === 0) return;

    const ctx = canvas.getContext('2d');
    const { width, height } = containerSize;
    
    // Set canvas size only if changed
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
    }
    
    // Clear canvas
    ctx.clearRect(0, 0, width, height);
    
    const padding = 40;
    const chartWidth = width - 2 * padding;
    const chartHeight = height - 2 * padding - (showLegend ? 30 : 0) - (title ? 25 : 0);
    
    if (chartWidth <= 0 || chartHeight <= 0) return;

    // Calculate value range
    let minValue = Infinity;
    let maxValue = -Infinity;
    
    data.forEach(tagData => {
      tagData.forEach(point => {
        minValue = Math.min(minValue, point.value);
        maxValue = Math.max(maxValue, point.value);
      });
    });
    
    if (yAxisMin !== 'auto') minValue = parseFloat(yAxisMin);
    if (yAxisMax !== 'auto') maxValue = parseFloat(yAxisMax);
    
    const valueRange = maxValue - minValue || 1;
    
    // Calculate time range
    let minTime = Infinity;
    let maxTime = -Infinity;
    
    data.forEach(tagData => {
      if (tagData.length > 0) {
        minTime = Math.min(minTime, tagData[0].timestamp);
        maxTime = Math.max(maxTime, tagData[tagData.length - 1].timestamp);
      }
    });
    
    const timeRange = maxTime - minTime || 1;
    
    // Set font
    ctx.font = `${fontSize || 12}px ${fontFamily || 'Arial'}`;
    ctx.fillStyle = foreColor || '#333';
    
    // Draw title
    let yOffset = 0;
    if (title) {
      ctx.textAlign = 'center';
      ctx.fillText(title, width / 2, 20);
      yOffset = 25;
    }
    
    // Draw grid
    if (showGrid) {
      ctx.strokeStyle = 'rgba(200, 200, 200, 0.5)';
      ctx.lineWidth = 1;
      
      // Vertical grid lines
      for (let i = 0; i <= 10; i++) {
        const x = padding + (i * chartWidth / 10);
        ctx.beginPath();
        ctx.moveTo(x, padding + yOffset);
        ctx.lineTo(x, padding + yOffset + chartHeight);
        ctx.stroke();
      }
      
      // Horizontal grid lines
      for (let i = 0; i <= 5; i++) {
        const y = padding + yOffset + (i * chartHeight / 5);
        ctx.beginPath();
        ctx.moveTo(padding, y);
        ctx.lineTo(padding + chartWidth, y);
        ctx.stroke();
      }
    }
    
    // Draw axes
    ctx.strokeStyle = foreColor || '#333';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(padding, padding + yOffset);
    ctx.lineTo(padding, padding + yOffset + chartHeight);
    ctx.lineTo(padding + chartWidth, padding + yOffset + chartHeight);
    ctx.stroke();
    
    // Draw Y-axis labels
    ctx.fillStyle = foreColor || '#333';
    ctx.textAlign = 'right';
    ctx.font = `${(fontSize || 12) - 2}px ${fontFamily || 'Arial'}`;
    
    for (let i = 0; i <= 5; i++) {
      const y = padding + yOffset + (i * chartHeight / 5);
      const value = maxValue - (i * valueRange / 5);
      ctx.fillText(value.toFixed(1), padding - 5, y + 4);
    }
    
    // Draw X-axis labels (time)
    ctx.textAlign = 'center';
    for (let i = 0; i <= 5; i++) {
      const x = padding + (i * chartWidth / 5);
      const time = minTime + (i * timeRange / 5);
      const date = new Date(time);
      const timeLabel = date.toLocaleTimeString('tr-TR', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
      ctx.fillText(timeLabel, x, padding + yOffset + chartHeight + 15);
    }
    
    // Draw data lines
    Array.from(data.entries()).forEach(([tagId, tagData], index) => {
      if (tagData.length < 2) return;
      
      const color = lineColors[index % lineColors.length];
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.beginPath();
      
      tagData.forEach((point, i) => {
        const x = padding + ((point.timestamp - minTime) / timeRange) * chartWidth;
        const y = padding + yOffset + chartHeight - ((point.value - minValue) / valueRange) * chartHeight;
        
        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      });
      
      ctx.stroke();
    });
    
    // Draw legend with live values
    if (showLegend && tagIds.length > 0) {
      const legendY = height - 20;
      let legendX = padding;
      
      tagIds.forEach((tagId, index) => {
        const color = lineColors[index % lineColors.length];
        
        // Find tag name from available tags
        const tagInfo = availableTags.find(tag => tag.id == tagId);
        const tagName = tagInfo ? tagInfo.name : `Tag ${tagId}`;
        
        // Draw color box
        ctx.fillStyle = color;
        ctx.fillRect(legendX, legendY - 10, 15, 10);
        
        // Draw label (tag name instead of ID)
        ctx.fillStyle = foreColor || '#333';
        ctx.textAlign = 'left';
        ctx.fillText(tagName, legendX + 20, legendY - 2);
        
        legendX += Math.max(80, tagName.length * 8 + 30); // Dynamic spacing based on text length
      });
    }
    
  }, [data, containerSize, showGrid, showLegend, lineColors, yAxisMin, yAxisMax, title, foreColor, fontSize, fontFamily, availableTags]);

  // Call drawChart only when dependencies change
  useEffect(() => {
    drawChart();
  }, [drawChart]);

  return (
    <WidgetWrapper 
      backgroundColor={backgroundColor}
      foreColor={foreColor}
      borderWidth={borderWidth}
      borderColor={borderColor}
      borderRadius={borderRadius}
      fontSize={fontSize}
      fontWeight={fontWeight}
      fontFamily={fontFamily}
      opacity={opacity}
      padding={0}
      {...props}
    >
      <Box
        ref={containerRef}
        sx={{
          width: '100%',
          height: '100%',
          position: 'relative'
        }}
      >
        <canvas
          ref={canvasRef}
          style={{
            width: '100%',
            height: '100%',
            display: 'block'
          }}
        />
        
        {tagIds.length === 0 ? (
          <Typography
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              color: 'rgba(0,0,0,0.5)',
              textAlign: 'center'
            }}
          >
            No tags selected for trending
          </Typography>
        ) : !dataLoaded ? (
          <Typography
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              color: 'rgba(0,0,0,0.5)',
              textAlign: 'center'
            }}
          >
            Loading {dataMode === 'historical' ? 'historical' : 'simulation'} data...
          </Typography>
        ) : null}
      </Box>
    </WidgetWrapper>
  );
};

export default TrendChart;